import { useRef, useCallback, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import { CountryCode } from 'libphonenumber-js';
import { t, Trans } from '@lingui/macro';
import noop from 'lodash/noop';

import Modal from '@decisiv/ui-components/lib/components/Modal';
import PlusCircle from '@decisiv/iconix/lib/components/PlusCircle';
import { useNotifications } from '@decisiv/ui-components/lib/components/Notifications';
import { createForm, FormHandler } from 'components/Form';
import { useSetPrimaryDepotContactMutation } from 'pages/Management/Depot/ContactsTab/hooks/useSetPrimaryDepotContactMutation';
import parseBackendErrorMessage from 'utils/parseBackendErrorMessage';
import { formatPhoneNumber } from 'utils/phoneFormatter';

import ContactForm from '../ContactForm';
import DepotSelector, { ContactDepot } from './DepotSelector';
import { ModalContentWrapper } from './styles';
import { ErrorMessageBanner } from '../styles';

import validationSchema, {
  FormValues,
  codeToCountry,
} from '../ContactForm/schema';
import applyContactValidationErrors from '../applyContactValidationErrors';
import useUpdateGlobalContactMutation from './hooks/useUpdateGlobalContactMutation';
import useAddDepotsToContact from '../hooks/useAddDepotsToContact';
import useRemoveContactFromDepotMutation from './hooks/useRemoveContactFromDepotMutation';

const { Form, FormProvider } = createForm<FormValues>();

interface EditContactModalProps {
  onClose: () => void;
  initialDepots: ContactDepot[];
  accountId: string;
  depot?: {
    accountId?: string;
    depotId?: string;
    depotUuid?: string;
    depotName?: string;
  };
}

interface EditContactModalWrapperProps
  extends Omit<EditContactModalProps, 'initialDepots' | 'accountId'> {
  depot?: {
    accountId: string;
    depotId: string;
    depotUuid: string;
    depotName: string;
  };
  contact?: Maybe<{
    id: string;
    firstName: string;
    lastName: string;
    email?: string;
    mobilePhone?: string;
    mobilePhoneCountryCode?: string;
    notes?: string;
    address1?: string;
    address2?: string;
    country?: string;
    state?: string;
    city?: string;
    postalCode?: string;
    notificationPreferences: {
      emailNotificationEnabled: boolean;
      smsNotificationEnabled: boolean;
    };
    depots?: ContactDepot[];
  }>;
}

const EditContactModal = ({
  onClose,
  initialDepots,
  accountId,
  depot,
}: EditContactModalProps) => {
  const formRef = useRef<FormHandler<FormValues>>(null);
  const {
    setError,
    formState: { isValid },
  } = useFormContext();
  const { notify } = useNotifications();

  const [depots, setDepots] = useState<ContactDepot[]>(initialDepots);
  const [originalDepotIds] = useState<string[]>(initialDepots.map((d) => d.id));
  const [savingDepots, setSavingDepots] = useState(false);
  const [errorMessage, setErrorMessage] = useState<{
    title: string;
    description: string;
  } | null>(null);
  const [primaryDepotIds, setPrimaryDepotIds] = useState<string[]>(() =>
    initialDepots.filter((d) => d.primaryContact).map((d) => d.id),
  );

  const handlePrimaryChange = useCallback(
    (depotId: string, checked: boolean) => {
      setPrimaryDepotIds((prev) =>
        checked ? [...prev, depotId] : prev.filter((id) => id !== depotId),
      );
    },
    [],
  );

  const handleTriggerForm = async () => {
    await formRef.current?.submit();
  };

  const handleClose = useCallback(() => {
    setErrorMessage(null);
    onClose();
  }, [onClose]);

  const addDepotsToContact = useAddDepotsToContact();
  const [removeContactFromDepot] = useRemoveContactFromDepotMutation();
  const [setPrimaryDepotContact] = useSetPrimaryDepotContactMutation({
    refetchQueries: [
      'ManagedPlatformAccountContacts',
      'DepotContacts',
      'ActivityLogs',
    ],
  });

  const showUpdateFailure = useCallback(
    (description: string) =>
      setErrorMessage({ title: t`Failed to update contact.`, description }),
    [],
  );

  const [updateContact, { loading: updatingContact }] =
    useUpdateGlobalContactMutation({
      onCompleted: async (data: {
        updateGlobalContact?: UpdateGlobalContactMutationPayload;
      }) => {
        // Surface server-side validation errors on the form and stop
        const validationErrors = data?.updateGlobalContact?.errors ?? [];
        if (validationErrors.length) {
          const handled = applyContactValidationErrors(
            validationErrors,
            setError,
          );
          if (!handled) {
            showUpdateFailure(
              t`An unexpected error occurred. Please try again.`,
            );
          }
          return;
        }

        const contactId = data?.updateGlobalContact?.globalContact?.id;
        /* istanbul ignore next -- @preserve */
        if (!contactId) return;

        // Add/remove depot associations
        const currentDepotIds = new Set(depots.map((d) => d.id));
        const addedDepots = depots.filter(
          (d) => !originalDepotIds.includes(d.id),
        );
        const removedDepotIds = originalDepotIds.filter(
          (id) => !currentDepotIds.has(id),
        );

        const depotErrors: string[] = [];
        setSavingDepots(true);

        const addErrors = await addDepotsToContact(
          contactId,
          addedDepots,
          primaryDepotIds,
        );
        depotErrors.push(...addErrors);

        await Promise.all(
          removedDepotIds.map(async (depotId) => {
            try {
              await removeContactFromDepot({
                variables: {
                  contactId,
                  depotId,
                },
              });
            } catch {
              const depotName =
                initialDepots.find((d) => d.id === depotId)?.name ??
                /* istanbul ignore next -- @preserve */ depotId;
              depotErrors.push(depotName);
            }
          }),
        );

        // Set primary for existing depots (not newly added ones, which are handled by addDepotsToContact)
        const addedDepotIds = new Set(addedDepots.map((d) => d.id));
        const existingPrimaryDepotIds = primaryDepotIds.filter(
          (id) => originalDepotIds.includes(id) && !addedDepotIds.has(id),
        );
        await Promise.all(
          existingPrimaryDepotIds.map(async (depotId) => {
            try {
              await setPrimaryDepotContact({
                variables: {
                  depotId,
                  globalContactId: contactId,
                },
              });
            } catch {
              // Primary assignment failures are non-blocking
            }
          }),
        );

        setSavingDepots(false);

        if (depotErrors.length > 0) {
          notify(
            {
              title: t`Contact updated, but some depot associations failed.`,
              intent: 'caution',
              duration: 8000,
              onClose: noop,
            } as any,
            <Trans>Failed depots: {depotErrors.join(', ')}. Try again.</Trans>,
          );
        } else {
          notify({
            title: t`Contact updated successfully.`,
            intent: 'success',
            duration: 5000,
            onClose: noop,
          } as any);
        }

        onClose();
      },
    });

  const isSaving = updatingContact || savingDepots;

  const handleEditContact = useCallback(
    async (data: FormValues) => {
      setErrorMessage(null);
      try {
        await updateContact({
          variables: {
            id: data.id as string,
            assetGroupingId: depot?.depotId,
            globalContact: {
              firstName: data.firstName,
              lastName: data.lastName,
              email: data.email || null,
              mobilePhone: data.mobilePhone,
              mobilePhoneCountryCode: data.mobilePhone
                ? data.mobilePhoneCountryCode
                : null,
              notes: data.notes,
              address1: data.address1,
              address2: data.address2,
              country: data.country,
              state: data.state,
              city: data.city,
              postalCode: data.postalCode,
              notificationPreferences: {
                emailNotificationEnabled:
                  data.emailNotificationEnabled || false,
                smsNotificationEnabled: data.smsNotificationEnabled || false,
              },
            },
          },
        });
      } catch (error) {
        // If the mutation promise rejects rather than invoking onError,
        // surface the error here so the failed submit can't raise an unhandled rejection.
        showUpdateFailure(
          parseBackendErrorMessage(error) ||
            t`An unexpected error occurred. Please try again.`,
        );
      }
    },
    [updateContact, depot, showUpdateFailure],
  );

  const handleRemoveDepot = useCallback((depotId: string) => {
    setDepots((prev) => prev.filter((d) => d.id !== depotId));
  }, []);

  const depotsSection = (
    <DepotSelector
      accountId={accountId}
      depots={depots}
      onDepotsChange={setDepots}
      onRemoveDepot={handleRemoveDepot}
      primaryDepotIds={primaryDepotIds}
      onPrimaryChange={handlePrimaryChange}
    />
  );

  return (
    <Modal
      actions={[
        {
          text: t`Save`,
          onClick: handleTriggerForm,
          loading: isSaving,
          disabled: !isValid || isSaving,
          loadingText: t`Saving...`,
        },
        {
          text: t`Cancel`,
          onClick: handleClose,
        },
      ]}
      id="update-contact-modal"
      icon={PlusCircle}
      intent="information"
      title={t`Edit Contact`}
      visible
    >
      <ModalContentWrapper>
        {errorMessage && (
          <ErrorMessageBanner
            intent="danger"
            size="medium"
            title={errorMessage.title}
          >
            {errorMessage.description}
          </ErrorMessageBanner>
        )}
        <Form ref={formRef} onSubmit={handleEditContact}>
          <ContactForm depotsSection={depotsSection} />
        </Form>
      </ModalContentWrapper>
    </Modal>
  );
};

const EditContactModalWrapper = ({
  contact,
  onClose,
  depot,
}: EditContactModalWrapperProps) => {
  const { accountId: routeAccountId } = useParams();
  const accountId =
    depot?.accountId ?? routeAccountId ?? /* istanbul ignore next */ '';

  const initialValues: FormValues = {
    id: contact?.id || '',
    firstName: contact?.firstName || '',
    lastName: contact?.lastName || '',
    email: contact?.email || '',
    // Normalized on the way in, using the same country code the field itself
    // formats with: a stored value that was never formatted would otherwise be
    // resubmitted verbatim when any other field on the contact is edited (PD-4252).
    mobilePhone: formatPhoneNumber(
      contact?.mobilePhone || '',
      codeToCountry(contact?.mobilePhoneCountryCode || '+1') as CountryCode,
    ),
    mobilePhoneCountryCode: contact?.mobilePhoneCountryCode || undefined,
    notes: contact?.notes || undefined,
    emailNotificationEnabled:
      contact?.notificationPreferences?.emailNotificationEnabled || false,
    smsNotificationEnabled:
      contact?.notificationPreferences?.smsNotificationEnabled || false,
    address1: contact?.address1 || '',
    address2: contact?.address2 || '',
    country: contact?.country || '',
    state: contact?.state || '',
    city: contact?.city || '',
    postalCode: contact?.postalCode || '',
  };

  const initialDepots: ContactDepot[] = (contact?.depots ?? []).map((d) => ({
    id: d.id,
    name: d.name,
    city: d.city,
    state: d.state,
    platformAccountName: d.platformAccountName,
    primaryContact: d.primaryContact,
  }));

  return (
    <FormProvider
      initialValues={initialValues}
      validationSchema={validationSchema}
    >
      <EditContactModal
        onClose={onClose}
        initialDepots={initialDepots}
        accountId={accountId}
        depot={depot}
      />
    </FormProvider>
  );
};

export default EditContactModalWrapper;
